import { FC, useEffect, useState } from "react";
import { ListUtil } from "src/framework/commons/ListUtil";
import { ValidateUtil } from "src/framework/commons/ValidateUtil";
import { useObservable } from "src/framework/store/storeUtil";
import { cidades$, estados$ } from "src/store/global.store";
import { Cidade } from "src/components/geral/RegioesDataTable/types";
import { InputText } from "src/framework/components/forms/inputs/InputText";
import { InputComboBoxList } from "src/framework/components/forms/inputs/InputComboBoxList";
import { FormProjetoHibridoType } from "src/store/vendas/projetos/hibrido/hibrido.types";
import { TipoBateriaInput } from "./containers/TipoBateriaInput";
import { TaxaDescargaInput } from "./containers/TaxaDescargaInput";
import { Box, IconButton, Tooltip } from "@mui/material";
import { TableViewIcon } from "../../offgrid/FormClienteOffGrid/containers/TableViewIcon";
import { useProjetosHibridoStore } from "src/store/vendas/projetos/hibrido/hibrido.store";
import { TipoLigacaoInput } from "./containers/TipoLigacaoInput";
import { TensaoSaidaInput } from "./containers/TensaoSaidaInput";
import { CalendarMonth } from "@mui/icons-material";
import { InputDecimal3 } from "src/framework/components/forms/inputs/InputDecimal3";
import { ModeloModuloInput } from "./containers/ModeloModuloInput";
import { PosicaoModuloInput } from "./containers/PosicaoModuloInput";
import { TipoEstruturaInput } from "./containers/TipoEstruturaInput";
import { NOME_CLIENTE_MAX_LENGTH } from "../../constants";

interface FormClientProperties {
    errorState: string[];
    onChangeError: (errorState: any) => void
    formState: FormProjetoHibridoType
    onChangeValue: (formState: Partial<FormProjetoHibridoType>) => void
}

const CLIENTE_PROPOSTA = "cliente_proposta"
const EMAIL_PROPOSTA = "email_proposta"
const CIDADE_ID = "cidade_id"

export const FormClienteHibrido: FC<FormClientProperties> = ({ errorState, onChangeError, formState, onChangeValue }) => {
    const { changeModalsValue } = useProjetosHibridoStore(store => store.actions)

    const estadoListState = useObservable(estados$);
    const cidadeListState = useObservable(cidades$);

    const [estadoSelecionado, setEstadoSelecionado] = useState<string>("")

    useEffect(()=>{
        if (formState?.estado) {
            setEstadoSelecionado(formState.estado.id)
        }
    }, [formState?.estado])

    const validateEmail = (email: string) => {
        return !!String(email)
          .toLowerCase()
          .match(
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          );
    };

    return (
        <>
            <div className="row">
                <div className="X12 L6">
                    <InputText label="Título do projeto" fullWidth
                        value={formState?.titulo || ''}
                        defaultValue={formState?.titulo || ''}
                        maxLength={100}
                        onChange={(e: any) => {
                            onChangeValue({ titulo: e.target.value })
                        }}
                    />
                </div>
                <div className="X12 L6">
                    <InputText label="* Nome do cliente" fullWidth
                        value={formState?.cliente_proposta}
                        error={errorState.includes(CLIENTE_PROPOSTA)}
                        maxLength={NOME_CLIENTE_MAX_LENGTH}
                        defaultValue={formState?.cliente_proposta}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.cliente_proposta = e.target.value
                            onChangeValue(state)
                        }}
                        onKeyUp={(e: any) => {
                            if (e.target.value !== "") {
                                const idxError = errorState.findIndex((es) => es === CLIENTE_PROPOSTA)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError(errorState)
                                }
                            }
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputText label="E-mail" fullWidth
                        value={formState?.email_proposta}
                        error={errorState.includes(EMAIL_PROPOSTA)}
                        helperText={errorState.includes(EMAIL_PROPOSTA) ? "E-mail inválido" : ''}
                        inputProps={{
                            autocomplete: 'off',
                        }}
                        onChange={(e: any) => {
                            const state = { ...formState }
                            state.email_proposta = (String(e.target.value) || '').trim()
                            onChangeValue(state)
                        }}
                        onKeyDown={(e: any) => {
                            const spaceRegex = /\s/g
                            e.target.value = e.target.value.replace(spaceRegex, '')
                        }}
                        onKeyUp={(e: any) => {
                            let _errorState = [...errorState]
                            if (e.target.value !== "") {
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                if (ValidateUtil.isValidEmail(e.target.value) && validateEmail(e.target.value)) {
                                    _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                                } else {
                                    _errorState = ListUtil.addItem(_errorState, EMAIL_PROPOSTA)
                                }
                            }else{
                                _errorState = ListUtil.removeItem(_errorState, EMAIL_PROPOSTA)
                            }
                            onChangeError(_errorState)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-verticais-estado-cb`}
                        key={`listagem-verticais-estado-cb`}
                        label={"* UF"}
                        textField="nome"
                        valueField="id"
                        options={estadoListState?.data || []}
                        value={formState?.estado || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option.id} - ${option.nome}`
                        }}
                        onChange={(value: any) => {
                            const state = { ...formState };
                            if (value) {
                                state.isTrocaUf = false;

                                if(value.id !== state?.estado_original?.id){
                                    state.isTrocaUf = true;
                                    state.recalcular = true;
                                }

                                state.estado = value;
                                state.cidade = undefined;
                                state.cidade_id = undefined;

                                setEstadoSelecionado(String(value.id))

                            } else {
                                state.estado = null;
                                setEstadoSelecionado('')
                            }
                            onChangeValue(state)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <InputComboBoxList
                        id={`listagem-verticais-cidade-cb`}
                        key={`listagem-verticais-cidade-cb`}
                        label={"* Cidade"}
                        textField="nome"
                        valueField="id"
                        error={errorState.includes(CIDADE_ID)}
                        options={cidadeListState?.data?.filter((c: Cidade) => c.estado_id === estadoSelecionado) || []}
                        value={formState?.cidade || null}
                        autoComplete
                        renderOptions={(option: any) => {
                            return `${option?.nome}`
                        }}
                        renderOptionsLabel={(option: any) => {
                            return `${option?.nome}`
                        }}
                        onChange={(value: Cidade) => {
                            const state = { ...formState };
                            if (value) {
                                const idxError = errorState.findIndex((es) => es === CIDADE_ID)
                                if (idxError > -1) {
                                    errorState.splice(idxError, 1)
                                    onChangeError([...errorState])
                                }

                                if (state.cidade_id !== value?.id) {
                                    state.recalcular = true;
                                }

                                state.cidade = value;
                                state.cidade_id = value.id;
                            } else {
                                state.cidade = null;
                                state.cidade_id = undefined;
                            }

                            onChangeValue(state)
                        }}
                    />
                </div>

                <div className="X12 L4">
                    <TipoBateriaInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>
                <div className="X12 L4">
                    <TaxaDescargaInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>

                <Tooltip title={formState.tipo_bateria?.id === 0 ? "Indisponível para gerador sem bateria" : ""}>
                    <Box className="X12 L4" display={"flex"} alignItems="center">
                            <div className="X9 L10" style={{ position: "relative", padding: 0 }}>
                                <span onClick={() => {
                                    if (!formState.tipo_bateria || formState.tipo_bateria?.id === 0) return;
                                    changeModalsValue({ equipamentos: true })
                                }} style={{ cursor: 'pointer', display: "block", top: 0, left: 0, position: "absolute", zIndex: 10, width: "100%", height: "100%", backgroundColor: "transparent" }} />
                                <InputText
                                    style={{ marginTop: "13px" }}
                                    label="* Carga dos Equipamentos (kW/dia)"
                                    fullWidth
                                    value={formState?.total_carga_equipamentos_kw  ? Intl.NumberFormat('pt-BR', { maximumFractionDigits: 2 }).format(formState?.total_carga_equipamentos_kw) : ''}
                                    disabled
                                />
                            </div>
                            <div className="X3 L2">
                                <IconButton
                                    disabled={!formState.tipo_bateria || formState.tipo_bateria?.id === 0}
                                    onClick={() => changeModalsValue({ equipamentos: true })}
                                    size="large"
                                >
                                    <TableViewIcon />
                                </IconButton>
                            </div>
                    </Box>
                </Tooltip>

                <div className="X12 L4">
                    <TipoLigacaoInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>

                <div className="X12 L4">
                    <TensaoSaidaInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>

                <Box className="X12 L4" display={"flex"} alignItems="center">
                    <div className="X9 L10" style={{ position: "relative", padding: 0 }}>
                        <InputDecimal3
                            allowNegative={false}
                            style={{ marginTop: "13px" }}
                            label="* Potência desejada (kWp)"
                            onChange={(e: any) => {
                                const value = e.target.value;
                                if (isNaN(Number(value))) return;
                                const mudouValor = Number(value || 0) !== Number(formState.potencia_desejada_kw || 0)

                                if (mudouValor) {
                                    onChangeValue({
                                        potencia_desejada_kw: Number(value),
                                        potencia_desejada_w: Number(value || 0) * 1000 ,
                                        modo_potencia: 'manual',
                                        recalcular: true,
                                        consumo_meses: formState.consumo_meses ? formState.consumo_meses.map(mes => ({
                                            ...mes,
                                            value: 0,
                                        })) : []
                                     });
                                }
                            } }
                            fullWidth
                            value={formState.potencia_desejada_kw || ''}
                            decimal={2}
                            disabled={!formState.estado || !formState.cidade}
                        />
                    </div>
                    <div className="X3 L2">
                        <IconButton
                            disabled={!formState.estado || !formState.cidade}
                            onClick={() => changeModalsValue({ potencia_desejada: true })}
                            size="large"
                        >
                            <CalendarMonth fontSize="large" htmlColor="#2E7D32" />
                        </IconButton>
                    </div>
                </Box>

                <div className="X12 L4">
                    <ModeloModuloInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>

                <div className="X12 L4" style={{ marginTop: 15 }}>
                    <PosicaoModuloInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>

                <div className="X12 L4" style={{ marginTop: -2 }}>
                    <TipoEstruturaInput errorState={errorState} formState={formState} onChangeValue={onChangeValue} />
                </div>
            </div>
        </>
    );
}
